import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    TablePagination,
    Paper,
    Button,
    Checkbox,
    Container,
    TextField,
    Typography,
    Card,
    CardContent,
    Grid,
} from '@mui/material'
import Layout from '../../components/Layout'
import {
    addPayments,
    deletePayment,
    getCustomerAllPayments,
    getCustomerAllUnpaidOrders,
} from '../../store/actions/storeCreditAction'
import {
    GET_CUSTOMER_ALL_PAYMENTS,
    GET_CUSTOMER_ALL_UNPAID_ORDERS,
} from '../../store/types'
import { checkUserRole } from '../../common/checkUserRole'
import { roleListId, roleType } from '../../config/roleList'
import { DELETE_CONFIRM } from '../../constants/form'

/* ---------------- helpers ---------------- */

const calcPaid = (order) =>
    order.orderpayments?.reduce((sum, p) => sum + Number(p.amount), 0) || 0

/* ---------------- component ---------------- */

const PayWithCredit = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { id } = useParams()

    const { customerAllUnpaidOrders, customerAllPayments } = useSelector(
        (state) => state.storeCredit
    )
    const { userInfo } = useSelector((state) => state.auth)
    const { roleList } = useSelector((state) => state.user)

    const [selected, setSelected] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    /* pagination */
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    /* ---------------- data fetch ---------------- */

    useEffect(() => {
        dispatch(getCustomerAllUnpaidOrders(id))
        dispatch(getCustomerAllPayments(id))

        return () => {
            dispatch({ type: GET_CUSTOMER_ALL_UNPAID_ORDERS, payload: null })
            dispatch({ type: GET_CUSTOMER_ALL_PAYMENTS, payload: null })
        }
    }, [dispatch, id])

    /* ---------------- credit base values ---------------- */

    const creditBalance = Number(customerAllUnpaidOrders?.creditBalance || 0)

    /* ---------------- derived orders ---------------- */

    const ordersWithCalc = useMemo(() => {
        return (
            customerAllUnpaidOrders?.orders?.map((o) => {
                const paid = calcPaid(o)
                const due = Number(o.invoiceAmountDecimal) - paid
                return { ...o, paid, due }
            }) || []
        )
    }, [customerAllUnpaidOrders])

    /* ---------------- selected credit ---------------- */

    const usedCredit = useMemo(() => {
        return Object.values(selected).reduce(
            (sum, s) => sum + (s.checked ? Number(s.amount || 0) : 0),
            0
        )
    }, [selected])

    const creditAvailable = Math.max(0, creditBalance - usedCredit)

    const disableNewSelection = creditAvailable <= 0

    /* ---------------- helpers ---------------- */

    const calcRemainingCredit = useCallback(
        (excludeOrderId) => {
            return (
                creditBalance -
                Object.entries(selected).reduce((sum, [id, s]) => {
                    if (id === excludeOrderId) return sum
                    return sum + (s.checked ? Number(s.amount || 0) : 0)
                }, 0)
            )
        },
        [creditBalance, selected]
    )

    /* ---------------- handlers ---------------- */

    const toggleOrder = useCallback(
        (order) => {
            setSelected((prev) => {
                const remaining = Math.max(0, calcRemainingCredit(order.id))

                const amount = Math.min(order.due, remaining)

                return {
                    ...prev,
                    [order.id]: {
                        checked: !prev?.[order.id]?.checked,
                        amount,
                    },
                }
            })
        },
        [calcRemainingCredit]
    )

    const changeAmount = useCallback(
        (order, value) => {
            let amount = Number(value)
            if (amount < 0) amount = 0
            if (amount > order.due) amount = order.due

            const remaining = Math.max(0, calcRemainingCredit(order.id))

            if (amount > remaining) amount = remaining

            setSelected((prev) => ({
                ...prev,
                [order.id]: {
                    ...prev[order.id],
                    amount,
                },
            }))
        },
        [calcRemainingCredit]
    )

    const handleSubmit = useCallback(() => {
        if (isSubmitting) return

        const payments = Object.entries(selected)
            .filter(([, v]) => v.checked && v.amount > 0)
            .map(([orderId, v]) => ({
                orderId,
                amount: Number(v.amount),
            }))

        if (!payments.length) {
            alert('No payment selected')
            return
        }

        setIsSubmitting(true)

        dispatch(
            addPayments({
                payments,
                customerId: id,
            })
        ).finally(() => {
            setIsSubmitting(false)
            setSelected({})
        })
    }, [dispatch, id, selected, isSubmitting])

    const handleDelete = useCallback(
        (paymentId) => {
            if (window.confirm(DELETE_CONFIRM)) {
                dispatch(deletePayment(paymentId, id))
            }
        },
        [dispatch, id]
    )

    /* ---------------- pagination ---------------- */

    const pagedPayments = useMemo(() => {
        if (!customerAllPayments) return []
        if (rowsPerPage <= 0) return customerAllPayments
        return customerAllPayments.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        )
    }, [customerAllPayments, page, rowsPerPage])

    /* ---------------- render ---------------- */

    return (
        <Layout>
            <Container>
                <Button onClick={() => navigate(-1)}>Back</Button>

                {/* ---------- Credit Summary ---------- */}
                <Grid container spacing={3} mt={2}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle2">
                                    Credit Balance
                                </Typography>
                                <Typography variant="h4" fontWeight={700}>
                                    ${creditBalance.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                background:
                                    creditAvailable <= 0
                                        ? 'linear-gradient(135deg,#ff5252,#ff1744)'
                                        : creditAvailable < 50
                                        ? 'linear-gradient(135deg,#ff9800,#f57c00)'
                                        : 'linear-gradient(135deg,#4caf50,#2e7d32)',
                                color: '#fff',
                            }}
                        >
                            <CardContent>
                                <Typography variant="subtitle2">
                                    Credit Available
                                </Typography>
                                <Typography variant="h4" fontWeight={700}>
                                    ${creditAvailable.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ---------- Unpaid Orders ---------- */}
                {ordersWithCalc.length > 0 && (
                    <>
                        <Typography variant="h6" mt={4}>
                            Unpaid Orders
                        </Typography>

                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>Invoice No</TableCell>
                                        <TableCell>Order Amount</TableCell>
                                        <TableCell>Paid</TableCell>
                                        <TableCell>Due</TableCell>
                                        <TableCell>Pay Amount</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {ordersWithCalc.map((order) => {
                                        const sel = selected[order.id] || {}
                                        const disableCheckbox =
                                            !sel.checked && disableNewSelection

                                        return (
                                            <TableRow key={order.id}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={!!sel.checked}
                                                        disabled={
                                                            order.due <= 0 ||
                                                            disableCheckbox
                                                        }
                                                        onChange={() =>
                                                            toggleOrder(order)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {order.invoiceNumber}
                                                </TableCell>
                                                <TableCell>
                                                    $
                                                    {order.invoiceAmountDecimal}
                                                </TableCell>
                                                <TableCell>
                                                    ${order.paid.toFixed(2)}
                                                </TableCell>
                                                <TableCell>
                                                    ${order.due.toFixed(2)}
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        disabled={!sel.checked}
                                                        value={sel.amount || ''}
                                                        onChange={(e) =>
                                                            changeAmount(
                                                                order,
                                                                e.target.value
                                                            )
                                                        }
                                                        inputProps={{
                                                            min: 0,
                                                            step: 0.01,
                                                            style: {
                                                                textAlign:
                                                                    'right',
                                                            },
                                                        }}
                                                        sx={{ width: 120 }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Button
                            sx={{ mt: 2 }}
                            variant="contained"
                            disabled={isSubmitting}
                            onClick={handleSubmit}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </>
                )}

                {/* ---------- Payment History ---------- */}
                {pagedPayments.length > 0 && (
                    <>
                        <Typography variant="h6" mt={5}>
                            Payment History
                        </Typography>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Invoice No</TableCell>
                                        <TableCell>Order Amount</TableCell>
                                        <TableCell>Paid</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {pagedPayments.map((p) => (
                                        <TableRow key={p.id}>
                                            <TableCell>
                                                {p.order?.invoiceNumber}
                                            </TableCell>
                                            <TableCell>
                                                ${p.order?.invoiceAmountDecimal}
                                            </TableCell>
                                            <TableCell>${p.amount}</TableCell>
                                            <TableCell>
                                                {p.paymentDate}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() =>
                                                        checkUserRole(
                                                            userInfo,
                                                            roleList,
                                                            roleListId.ORDER_PAYMENT,
                                                            roleType.DELETE
                                                        )
                                                            ? handleDelete(p.id)
                                                            : alert(
                                                                  'Not Permit User'
                                                              )
                                                    }
                                                >
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>

                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            count={customerAllPayments.length}
                                            page={page}
                                            rowsPerPage={rowsPerPage}
                                            onPageChange={(_, p) => setPage(p)}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(+e.target.value)
                                                setPage(0)
                                            }}
                                        />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Container>
        </Layout>
    )
}

export default PayWithCredit
